import {
  Checkbox,
  HStack,
  Tag,
  TagLabel,
  Text,
  VStack,
} from "@chakra-ui/react";
import DurationField, { type DurationValue } from "components/ds/DurationField";
import type { MFRequestableRole } from "state/members/access_v2/catalogs";
import {
  chooseRoleNote,
  holdingNote,
  noRolesNote,
  type RequestFor,
  roleTermsLabel,
  roleTermsSource,
  sideEffectSummary,
} from "state/members/access_v2/roleRequests";

export type RoleDurationControl = {
  value: (role: MFRequestableRole) => DurationValue;
  onChange: (roleId: string, duration: DurationValue) => void;
};

// The roles a member can request on a resource whose access follows its
// roles. Holding notes describe one person, so they are only shown when the
// picker is for one person. With `durations`, each ticked role takes its own.
const RolePicker = ({
  roles,
  selectedIds,
  onToggle,
  showHolding,
  requestFor = "you",
  label = "Roles",
  durations,
}: {
  roles: MFRequestableRole[];
  selectedIds: string[];
  onToggle: (roleId: string) => void;
  showHolding: boolean;
  requestFor?: RequestFor;
  label?: string;
  durations?: RoleDurationControl;
}) => (
  <VStack w="full" align="flex-start">
    <Text variant="label">{label}</Text>
    {roles.length === 0 ? (
      <Text variant="lil">{noRolesNote(requestFor)}</Text>
    ) : (
      <>
        <Text variant="lil">{chooseRoleNote(requestFor)}</Text>
        <VStack
          w="full"
          align="flex-start"
          spacing={3}
          px={4}
          py={3}
          border="1px solid"
          borderColor="gray.200"
          rounded={6}
        >
          {roles.map((role) => {
            const note = showHolding ? holdingNote(role.holding, requestFor) : null;
            const sideEffects = sideEffectSummary(role);
            const source = roleTermsSource(role);
            const ticked = selectedIds.includes(role.id);
            return (
              <VStack key={role.id} align="flex-start" spacing={2}>
                <Checkbox
                  alignItems="flex-start"
                  isChecked={ticked}
                  onChange={() => onToggle(role.id)}
                >
                  <VStack align="flex-start" spacing={1}>
                    <HStack spacing={2}>
                      <Text fontSize="sm" fontWeight={500}>
                        {role.name}
                      </Text>
                      <Tag size="sm" flexShrink={0}>
                        <TagLabel>{roleTermsLabel(role.terms)}</TagLabel>
                      </Tag>
                    </HStack>
                    {source && <Text variant="lil">{source}</Text>}
                    {role.description && (
                      <Text variant="lil">{role.description}</Text>
                    )}
                    {note && <Text variant="lil">{note}</Text>}
                    {sideEffects && <Text variant="lil">{sideEffects}</Text>}
                  </VStack>
                </Checkbox>
                {durations && ticked && (
                  <HStack pl={6} spacing={2}>
                    <Text variant="lil">For</Text>
                    <DurationField
                      size="sm"
                      value={durations.value(role)}
                      allowIndefinite={
                        role.terms.allowIndefinite || role.terms.indefiniteOnly
                      }
                      onChange={(d) => durations.onChange(role.id, d)}
                    />
                  </HStack>
                )}
              </VStack>
            );
          })}
        </VStack>
      </>
    )}
  </VStack>
);

export default RolePicker;
